import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface StatsLoaderProps {
  /**
   * The type of stats being loaded
   */
  type?: "metric" | "text" | "badge";
  /**
   * Custom width for the skeleton
   */
  width?: string | number;
  /**
   * Custom height for the skeleton
   */
  height?: string | number;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Number of stat items to show (for lists)
   */
  count?: number;
  /**
   * Layout direction
   */
  direction?: "horizontal" | "vertical";
}

/**
 * StatsLoader - A reusable component for showing loading states for statistics
 * Provides consistent loading skeletons across the app for stat displays
 */
export const StatsLoader: React.FC<StatsLoaderProps> = ({
  type = "metric",
  width,
  height,
  className = "",
  count = 1,
  direction = "horizontal",
}) => {
  const getSkeletonDimensions = () => {
    switch (type) {
      case "metric":
        return {
          width: width || "60px",
          height: height || "24px",
        };
      case "text":
        return {
          width: width || "80px",
          height: height || "16px",
        };
      case "badge":
        return {
          width: width || "48px",
          height: height || "20px",
        };
      default:
        return {
          width: width || "60px",
          height: height || "20px",
        };
    }
  };

  const dimensions = getSkeletonDimensions();
  const containerClass =
    direction === "vertical" ? "space-y-2" : "space-x-3 flex items-center";

  if (count > 1) {
    return (
      <div className={`${containerClass} ${className}`}>
        {Array.from({ length: count }).map((_, index) => (
          <Skeleton
            key={index}
            className={`${type === "badge" ? "rounded-full" : ""}`}
            style={{ width: dimensions.width, height: dimensions.height }}
          />
        ))}
      </div>
    );
  }

  return (
    <Skeleton
      className={`${type === "badge" ? "rounded-full" : ""} ${className}`}
      style={{ width: dimensions.width, height: dimensions.height }}
    />
  );
};

/**
 * VoiceStatsLoader - Specialized loader for voice agent stats
 * Shows skeleton for call count, duration, and cost
 */
export const VoiceStatsLoader: React.FC<{ className?: string }> = ({
  className = "",
}) => {
  return (
    <div className={`flex items-center space-x-4 ${className}`}>
      {/* Total calls skeleton */}
      <div className="flex items-center space-x-1">
        <Skeleton className="h-4 w-4 rounded" />
        <StatsLoader type="metric" width="32px" />
      </div>

      {/* Average duration skeleton */}
      <div className="flex items-center space-x-1">
        <Skeleton className="h-4 w-4 rounded" />
        <StatsLoader type="text" width="45px" />
      </div>

      {/* Today's calls skeleton */}
      <div className="flex items-center space-x-1">
        <Skeleton className="h-4 w-4 rounded" />
        <StatsLoader type="metric" width="24px" />
      </div>
    </div>
  );
};

/**
 * ChatStatsLoader - Specialized loader for chat agent stats
 * Shows skeleton for conversation counts
 */
export const ChatStatsLoader: React.FC<{ className?: string }> = ({
  className = "",
}) => {
  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <StatsLoader type="metric" width="40px" />
      <span className="text-muted-foreground">•</span>
      <StatsLoader type="text" width="60px" />
    </div>
  );
};
